<template>
  <div class="collection-item-list" :style="{ '--minGridItemWidth': `${MIN_GRID_ITEM_WIDTH}px` }">
    <template v-if="!loading || items.length">
      <DynamicScroller
        ref="scroller"
        :items="displayItems"
        :min-item-size="minItemSize"
        key-field="_id"
        class="scroller"
        page-mode
        @resize="onScrollerResize"
        @visible="onScrollerMounted"
      >
        <template v-if="isGridLayout" #default="{ item: chunk, index, active }">
          <DynamicScrollerItem
            :item="chunk"
            :active="active"
            :index="index"
            :data-index="index"
            class="grid-row pb-6"
          >
            <CollectionItemFactory
              v-for="(item, i) in chunk.items"
              :key="`grid-row-item--${i}`"
              :item="item"
              :list-style="listStyle"
              :user-id="userId"
              :membership="membership"
              @action="$emit('action', item, $event)"
              @filter-tags="$emit('filter-tags', $event)"
            />
          </DynamicScrollerItem>
        </template>

        <template v-else #default="{ item, index, active }">
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :index="index"
            :data-index="index"
            class="pb-6"
          >
            <!-- NEVER key direct children of DynamicScrollerItem -->
            <CollectionItemFactory
              :item="item"
              :list-style="listStyle"
              :user-id="userId"
              :membership="membership"
              @action="$emit('action', item, $event)"
              @filter-tags="$emit('filter-tags', $event)"
            />

            <v-divider class="mt-6" color="neutral50" />
          </DynamicScrollerItem>
        </template>
      </DynamicScroller>
    </template>

    <div v-else class="loaders" :class="{ 'grid-row': isGridLayout }">
      <div v-for="i in 15" :key="`loader-${i}`">
        <CollectionItemSkeleton :list-style="listStyle" />
        <v-divider class="my-6" color="neutral50" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, defineProps, defineEmits } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import chunk from 'lodash.chunk'
import DynamicScroller from '@/components/global/DynamicScroller.vue'
import { DynamicScrollerItem } from 'vue-virtual-scroller'

import { ITEM_LAYOUT_STYLE } from '@/utils/constants'
import { debounce } from '@/utils/helpers'
import CollectionItemFactory from './CollectionItemFactory.vue'
import CollectionItemSkeleton from './CollectionItemSkeleton.vue'

const props = defineProps({
  loading: Boolean,
  items: {
    type: Array,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  listStyle: {
    type: String,
    default: 'card',
  },
})

// Define emits for documentation purposes
defineEmits(['action', 'filter-tags'])

const store = useStore()
const display = useDisplay()
const scroller = ref(null)
const scrollerWidth = ref(0)
const GRID_GAP = 32

const isExtensionPopup = computed(() => store.getters['device/isExtensionPopup'])

const MIN_GRID_ITEM_WIDTH = computed(() => {
  if (isExtensionPopup.value) return 240
  return props.listStyle === ITEM_LAYOUT_STYLE.tiles ? 240 : 300
})

const isGridLayout = computed(() => {
  return (
    [ITEM_LAYOUT_STYLE.cards, ITEM_LAYOUT_STYLE.tiles].includes(props.listStyle) &&
    display.smAndUp.value
  )
})

const minItemSize = computed(() => {
  switch (props.listStyle) {
    case ITEM_LAYOUT_STYLE.cards:
      return 300
    case ITEM_LAYOUT_STYLE.tiles:
      return 200
    case ITEM_LAYOUT_STYLE.list:
      return 80
    case ITEM_LAYOUT_STYLE.headlines:
      return 90
    default:
      return 0
  }
})

const gridChunks = computed(() => {
  const width = scrollerWidth.value
  const minWidth = MIN_GRID_ITEM_WIDTH.value
  if (!width) return []

  let itemsPerRow = Math.floor(width / minWidth)
  const totalItemGap = Math.max(2, itemsPerRow - 1) * GRID_GAP

  const isOverflowing = itemsPerRow * minWidth + totalItemGap > width
  if (isOverflowing) itemsPerRow -= 1

  const chunks = chunk(props.items, itemsPerRow)

  return chunks.map((set, i) => ({
    _id: `chunk--${i}`,
    id: `chunk--${i}`,
    items: set,
  }))
})

const displayItems = computed(() => {
  if (isGridLayout.value) return gridChunks.value
  return props.items
})

function updateScrollerWidth() {
  const $scroller = scroller.value?.$el
  if (!$scroller) return

  const width = $scroller?.clientWidth ?? 0

  if (scrollerWidth.value !== width) {
    scrollerWidth.value = width
  }
}

function onScrollerMounted() {
  updateScrollerWidth()
}

const onScrollerResize = debounce(async function onScrollerResize() {
  await nextTick()
  updateScrollerWidth()
}, 50)
</script>

<style lang="scss" scoped>
.collection-item-list {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.scroller {
  height: 100%;
  overflow-y: auto;
}

.grid-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(var(--minGridItemWidth), 1fr));
  grid-gap: 32px;

  &.loaders {
    .v-divider {
      display: none;
    }
  }
}
</style>
